if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // --- NEW: quantity validation wiring ---
        this.dynamicCheckoutButton = this.querySelector('.shopify-payment-button button, .shopify-payment-button__button');
        this.locateQtyInput();
        this.updateButtonsForQty = this.updateButtonsForQty.bind(this);
        if (this.qtyInput) {
          this.qtyInput.addEventListener('input', this.updateButtonsForQty);
          this.qtyInput.addEventListener('change', this.updateButtonsForQty);
        }
        this.updateButtonsForQty(); // initialize
      }

      // Tries common locations/selectors; supports when the qty input is outside the <form>
      locateQtyInput() {
        const sectionId = this.dataset.sectionId;
        this.qtyInput =
          this.form.querySelector('[name="quantity"]') ||
          this.querySelector('[name="quantity"]') ||
          (sectionId && document.getElementById(`Quantity-${sectionId}`)) ||
          (sectionId && this.closest(`#shopify-section-${sectionId}`)?.querySelector('[name="quantity"], .quantity__input')) ||
          (sectionId && document.querySelector(`[data-section-id="${sectionId}"] [name="quantity"]`)) ||
          null;
      }

      getMinQty() {
        if (!this.qtyInput) return 1;
        const minAttr = parseFloat(this.qtyInput.min);
        return Number.isFinite(minAttr) && minAttr > 0 ? minAttr : 1;
      }

      isQtyValid() {
        if (!this.qtyInput) return true; // No qty input present; allow default behavior
        const val = parseFloat(this.qtyInput.value);
        return Number.isFinite(val) && val >= this.getMinQty();
      }

      updateButtonsForQty() {
        const valid = this.isQtyValid();

        if (this.submitButton) {
          this.submitButton.toggleAttribute('disabled', !valid);
          this.submitButton.setAttribute('aria-disabled', valid ? 'false' : 'true');
        }
        if (this.dynamicCheckoutButton) {
          this.dynamicCheckoutButton.toggleAttribute('disabled', !valid);
        }

        this.classList.toggle('product-form--invalid', !valid);

        // Clear any previous error when the value becomes valid
        if (valid) this.handleErrorMessage(false);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // Block invalid quantities BEFORE any loading state/fetch
        if (!this.isQtyValid()) {
          const min = this.getMinQty();
          this.handleErrorMessage(`Please enter a quantity of at least ${min}.`);
          this.updateButtonsForQty();
          if (this.qtyInput) this.qtyInput.focus();
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // If the qty input lives OUTSIDE the <form>, include its value in the payload
        if (this.qtyInput && !this.form.querySelector('[name="quantity"]')) {
          const q = parseInt(this.qtyInput.value, 10);
          formData.set('quantity', Number.isFinite(q) && q >= this.getMinQty() ? q : this.getMinQty());
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}



